/**
 * @fileoverview Template to Document Service
 * Handles converting template library items into supporting documents
 */

import { getEvidenceUrl } from '@/app/pages/assurancelibrary/downloadFromSDK.module';
import { createDocument } from './documentsService';
import { SupportingDocs } from '../js/external-js/variableModules/namespaces/SupportingDocs.namespace';
import type { Template } from './templateService';
import { getAuthUID } from '@/lib/authState';

/**
 * Downloads a template file and converts it to a File object
 */
async function downloadTemplateAsFile(template: Template): Promise<File> {
  try {
    console.log('[TemplateToDocumentService] Templates:', template);
    console.log('[TemplateToDocumentService] Downloading template:', template.name);
    console.log('[TemplateToDocumentService] Download URL:', template.downloadUrl);
    console.log('[TemplateToDocumentService] File name:', template.templateData.fileName);
    
    // Use getEvidenceUrl to get the blob URL for the template file
    // The path parameter should be the full path to the file in Firebase Storage
    const fullPath = `templates/templateLibrary/documents/${decodeURIComponent(template.downloadUrl)}`;
    
    const blobUrl = await getEvidenceUrl({
      fileName: '', // Not used when path contains the full file path
      path: fullPath // Full path to the file in Firebase Storage
    });
    
    console.log('[TemplateToDocumentService] Got blob URL:', blobUrl);
    
    // Fetch the blob from the URL
    const response = await fetch(blobUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch template file: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    
    // Create a File object from the blob
    // Handle dateUploaded which might be a string, Firestore timestamp, or Date
    let lastModified = Date.now(); // Default to current time
    if (template.templateData.dateUploaded) {
      if (typeof template.templateData.dateUploaded === 'string') {
        lastModified = new Date(template.templateData.dateUploaded).getTime();
      } else if (template.templateData.dateUploaded instanceof Date) {
        lastModified = template.templateData.dateUploaded.getTime();
      } else if (template.templateData.dateUploaded.toDate) {
        // Firestore timestamp
        lastModified = template.templateData.dateUploaded.toDate().getTime();
      }
    }
    
    const file = new File([blob], template.templateData.fileName, {
      type: getContentType(template.templateData.fileName),
      lastModified: lastModified
    });
    
    console.log('[TemplateToDocumentService] Template downloaded as file:', file.name, file.size);
    return file;
    
  } catch (error) {
    console.error('[TemplateToDocumentService] Error downloading template:', error);
    throw new Error(`Failed to download template: ${error.message}`);
  }
}

/**
 * Creates a document template object from template metadata
 */
function createDocumentFromTemplate(template: Template, companyID: string): SupportingDocs.Document {
  // Generate filename from the processed template name to avoid duplication
  const originalExtension = template.templateData.fileName.split('.').pop() || 'docx';
  const fileName = `${template.name}.${originalExtension}`;
  
  // Create a new document instance with template data
  // DED-1208: Get documentType from template data (try documentType first, then type, then fallback)
  const templateData = template.templateData as any;
  const documentType = templateData.documentType || templateData.type || 'Policy';
  
  // DED-1208: Get procRef (related controls) from template data
  const procRef = templateData.procRef || [];
  
  const documentTemplate = {
    // Basic document properties
    docID: `template-${template.id}-${Date.now()}`, // Unique ID for the new document
    fileName: fileName,
    documentType: documentType, // DED-1208: Use document type from template data
    classification: template.templateData.classification,
    
    // DED-1208: Include procRef for related controls
    procRef: procRef,
    
    // Template metadata with company-specific processing
    title: template.name, // Already processed with company name
    description: template.description,
    comments: '', // DED-1208: Comments should be blank when creating from template
    
    // Dates
    dateAdded: new Date(),
    dateUploaded: new Date(),
    lastReviewDate: new Date(),
    nextReviewDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year from now
    
    // User and company info
    uploadedBy: getAuthUID(),
    customerID: companyID,
    
    // Template-specific metadata
    templateSource: {
      templateId: template.id,
      templateName: template.name, // Already processed with company name
      templateVersion: template.metadata.version,
      templateAuthor: template.metadata.author,
      originalUploadDate: template.templateData.dateUploaded
    },
    
    // Status
    status: 'Active',
    
    // Additional metadata from template
    tags: template.metadata.tags,
    version: template.metadata.version,
    
    // Document flags
    stickyDoc: template.templateData.stickyDoc,
    
    // File info
    fileSize: (template.metadata as any).fileSize || 0,
    docHash: template.templateData.docHash
    
  } as any; // Cast to any to match the SupportingDocs.Document interface
  
  return documentTemplate;
}

/**
 * Gets content type based on file extension
 */
function getContentType(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop();
  const contentTypes: { [key: string]: string } = {
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'doc': 'application/msword',
    'pdf': 'application/pdf',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xls': 'application/vnd.ms-excel',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'ppt': 'application/vnd.ms-powerpoint'
  };
  return contentTypes[ext || ''] || 'application/octet-stream';
}

/**
 * Main function to convert a template to a supporting document
 */
export async function useTemplateAsDocument(
  template: Template, 
  companyID: string,
  options: {
    documentType?: string;
    classification?: string;
    customTitle?: string;
    customDescription?: string;
  } = {}
): Promise<SupportingDocs.Document> {
  try {
    console.log('[TemplateToDocumentService] Converting template to document:', template.name);
    
    // Step 1: Download the template file
    const templateFile = await downloadTemplateAsFile(template);
    
    // Step 2: Create document metadata from template
    const documentTemplate = createDocumentFromTemplate(template, companyID);
    
    // Apply any custom options
    if (options.documentType) {
      documentTemplate.documentType = options.documentType;
    }
    if (options.classification) {
      documentTemplate.classification = options.classification;
    }
    if (options.customTitle) {
      documentTemplate.title = options.customTitle;
    }
    if (options.customDescription) {
      documentTemplate.description = options.customDescription;
    }
    
    // Step 3: Create the document in the documents service
    console.log('[TemplateToDocumentService] Creating document with file:', templateFile.name);
    const createdDocument = await createDocument(documentTemplate, templateFile, companyID);
    
    console.log('[TemplateToDocumentService] Successfully created document:', createdDocument.docID);
    return createdDocument;
    
  } catch (error) {
    console.error('[TemplateToDocumentService] Error converting template to document:', error);
    throw new Error(`Failed to use template as document: ${error.message}`);
  }
}

/**
 * Batch convert multiple templates to documents
 */
export async function useMultipleTemplatesAsDocuments(
  templates: Template[],
  companyID: string,
  options: {
    documentType?: string;
    classification?: string;
  } = {}
): Promise<{
  successful: SupportingDocs.Document[];
  failed: { template: Template; error: string }[];
}> {
  const successful: SupportingDocs.Document[] = [];
  const failed: { template: Template; error: string }[] = [];
  
  for (const template of templates) {
    try {
      const document = await useTemplateAsDocument(template, companyID, options);
      successful.push(document);
    } catch (error) {
      failed.push({
        template,
        error: error.message
      });
    }
  }
  
  return { successful, failed };
}

export default {
  useTemplateAsDocument,
  useMultipleTemplatesAsDocuments
};
